import { del, get, patch, put, post } from "./api";

// Helper function to safely extract numeric ID from identifier
const extractNumericId = (identifier: string): string => {
  if (typeof identifier !== "string") {
    return identifier?.toString() || "0";
  }
  const match = identifier.match(/\d+$/);
  return match ? match[0] : identifier;
};

// Helper function to safely convert to integer
const safeParseInt = (value: any): number => {
  if (typeof value === "number") return Math.floor(value);
  const parsed = parseInt(value?.toString() || "0", 10);
  return isNaN(parsed) ? 0 : parsed;
};

// Enhanced empty value detection
const isEmptyValue = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value))
    return value.length === 0 || value.every(isEmptyValue);
  if (typeof value === "object" && value.data !== undefined) {
    return isEmptyValue(value.data);
  }
  return false;
};

export const getDataForCleanup = async (id) => {
  try {
    const url = `/projects/${id}/data`;
    const data = await get(url);
    console.log("Returned data", data);
    return data;
  } catch (error) {
    console.error("Error fetching cleanup data:", error);
    throw error;
  }
};

const checkType = (value, type) => {
  if (Array.isArray(value)) {
    return value.includes(type);
  } else {
    return value == type;
  }
};

export const getNamedEntities = async (
  id: string,
  type = "org",
  exclude = []
) => {
  try {
    let url = `/projects/${id}/names`;
    if (!checkType(type, "org")) {
      const excludeParam = exclude.length > 0 ? exclude.join(",") : "";
      url = `${url}?type=${encodeURIComponent(
        type
      )}&exclude=${encodeURIComponent(excludeParam)}`;
    }
    console.log("Fetching named entities:", url);
    const data = await get(url);
    console.log("getNamedEntities result:", data);
    return data;
  } catch (error) {
    console.error("Error fetching named entities:", error);
    throw error;
  }
};

export const updateName = async (
  projectId: string,
  nameId: string,
  value: string
) => {
  try {
    const encodedNameId = encodeURIComponent(nameId);
    const url = `/projects/${projectId}/names/${encodedNameId}`;

    console.log("Updating name:", { projectId, nameId, value, url });

    const result = await put(url, { name: value.trim() });
    console.log("Update name result:", result);

    return result;
  } catch (error) {
    console.error("Error updating name:", error);
    throw error;
  }
};

export const updateAnswerForUser = async (
  projectId: string,
  user: string,
  set: string,
  answers: string[],
  identifier: string
) => {
  let url = `/projects/${projectId}/data/${user}`;
  const data = { set, identifier, answers };
  const result = await patch(url, data);
  return result;
};

export const updateSpecificAnswer = async (
  projectId: string,
  user: string,
  set: string,
  identifier: string,
  item: number,
  innerIndex: number,
  newValue: string
) => {
  try {
    const url = `/projects/${projectId}/data/update-answer`;

    // Clean and validate parameters
    const cleanSet = extractNumericId(set.toString());
    const cleanIdentifier = extractNumericId(identifier.toString());
    const safeItem = safeParseInt(item);
    const safeInnerIndex = safeParseInt(innerIndex);

    const data = {
      user: user.trim(),
      projectId: projectId.trim(),
      set: cleanSet,
      identifier: cleanIdentifier,
      item: safeItem,
      innerIndex: safeInnerIndex,
      newValue: newValue.trim(),
    };

    console.log("=== FRONTEND UPDATE ANSWER ===");
    console.log("Updating specific answer with data:", data);

    const result = await post(url, data);
    console.log("Update answer result:", result);

    if (result && result.success) {
      console.log("✅ Update successful");
    } else {
      console.error("❌ Update failed:", result);
    }

    return result;
  } catch (error) {
    console.error("❌ Update error:", error);
    throw error;
  }
};

export const deleteUserData = async (projectId: string, user: string) => {
  let url = `/projects/${projectId}/data/${user}`;
  const data = await del(url);
  return data;
};

export const deleteName = async (projectId: string, nameId: string) => {
  nameId = encodeURIComponent(nameId);
  let url = `/projects/${projectId}/names/${nameId}`;
  const data = await del(url);
  return data;
};

export const deleteSet = async (
  user: string,
  projectId: string,
  setIndex: string
) => {
  try {
    const url = `/projects/${projectId}/data/delete-set`;

    // Clean the set index
    const cleanSetIndex = extractNumericId(setIndex.toString());

    const data = {
      user: user.trim(),
      data: {
        projectId: projectId.trim(),
        setIndex: cleanSetIndex,
      },
    };

    console.log("=== FRONTEND DELETE SET ===");
    console.log("Deleting set with data:", data);

    const result = await post(url, data);
    console.log("Delete set result:", result);

    if (result && result.success) {
      console.log("✅ Delete set successful");
    } else {
      console.error("❌ Delete set failed:", result);
    }

    return result;
  } catch (error) {
    console.error("❌ Delete set error:", error);
    throw error;
  }
};

export const deleteAnswer = async (
  user: string,
  projectId: string,
  set: string,
  identifier: string,
  item: number,
  innerIndex: number
) => {
  try {
    const url = `/projects/${projectId}/data/delete-answer`;

    const cleanSet = extractNumericId(set.toString());
    const cleanIdentifier = extractNumericId(identifier.toString());
    const safeItem = safeParseInt(item);
    const safeInnerIndex = safeParseInt(innerIndex);

    const data = {
      user: user.trim(),
      data: {
        projectId: projectId.trim(),
        set: cleanSet,
        identifier: cleanIdentifier,
        item: safeItem,
        innerIndex: safeInnerIndex,
      },
    };

    console.log("=== FRONTEND DELETE ANSWER ===");
    console.log("Deleting answer with data:", data);

    const result = await post(url, data);
    console.log("Delete answer result:", result);

    if (result && result.success) {
      console.log("✅ Delete successful");
    } else {
      console.error("❌ Delete failed:", result);
    }

    return result;
  } catch (error) {
    console.error("❌ Delete error:", error);
    throw error;
  }
};

//rough will change when i know what the structure is
export const insertAnswer = async (
  user: string,
  projectId: string,
  set: string,
  identifier: string,
  answer: string
) => {
  let url = `/projects/${projectId}/data/insert-answer`;
  const data = { user, projectId, set, identifier, answer };
  console.log(data);
  const result = await post(url, data);
  return result;
};

export const pseudoAnswer = async (
  user: string,
  projectId: string,
  set: string,
  identifier: string,
  item: number,
  innerIndex: number
) => {
  try {
    const url = `/projects/${projectId}/data/pseudo-answer`;

    const cleanSet = extractNumericId(set.toString());
    const cleanIdentifier = extractNumericId(identifier.toString());
    const safeItem = safeParseInt(item);
    const safeInnerIndex = safeParseInt(innerIndex);

    const data = {
      user: user.trim(),
      data: {
        projectId: projectId.trim(),
        set: cleanSet,
        identifier: cleanIdentifier,
        item: safeItem,
        innerIndex: safeInnerIndex,
      },
    };

    console.log("=== FRONTEND PSEUDO ANSWER ===");
    console.log("Pseudonymizing answer with data:", data);

    const result = await post(url, data);
    console.log("Pseudo answer result:", result);

    return result;
  } catch (error) {
    console.error("❌ Pseudo answer error:", error);
    throw error;
  }
};

export const isPseudoAnswer = async (
  user: string,
  projectId: string,
  set: string,
  identifier: string,
  item: number,
  innerIndex: number
) => {
  try {
    const url = `/projects/${projectId}/data/is-pseudo-answer`;

    const cleanSet = extractNumericId(set.toString());
    const cleanIdentifier = extractNumericId(identifier.toString());
    const safeItem = safeParseInt(item);
    const safeInnerIndex = safeParseInt(innerIndex);

    const data = {
      user: user.trim(),
      data: {
        projectId: projectId.trim(),
        set: cleanSet,
        identifier: cleanIdentifier,
        item: safeItem,
        innerIndex: safeInnerIndex,
      },
    };

    //console.log("Checking if answer is pseudo with data:", data);

    const result = await post(url, data); // Use POST instead of GET
    //console.log("Is pseudo answer result:", result);

    return result;
  } catch (error) {
    console.error("❌ Is pseudo answer error:", error);
    throw error;
  }
};

//this has become all editabel
export const onlyEditable = async (data, questionIdentifiers, allQuestions) => {
  try {
    console.log("=== PROCESSING EDITABLE DATA ===");
    console.log("Input data length:", data?.length || 0);
    console.log("Question identifiers:", questionIdentifiers);
    console.log("All questions count:", allQuestions?.length || 0);

    if (!Array.isArray(data) || !Array.isArray(allQuestions)) {
      console.error("Invalid input data format");
      return [];
    }

    const processedData = data
      .map((userData) => {
        if (!userData || !userData.data) {
          console.log(`User ${userData?.user || "unknown"}: No data found`);
          return { ...userData, data: [] };
        }

        /*console.log(
          `Processing user ${userData.user} with ${userData.data.length} sets`
        );*/

        // Function to flatten multi-dimensional arrays and track indices
        const flattenAnswersWithIndices = (answers: any[]) => {
          if (!Array.isArray(answers)) return [];

          return answers.flatMap((answer, outer) => {
            if (Array.isArray(answer)) {
              return answer
                .map((innerAnswer, inner) => ({
                  answer: innerAnswer,
                  outer,
                  inner,
                }))
                .filter((item) => !isEmptyValue(item.answer));
            } else {
              if (isEmptyValue(answer)) {
                return [];
              }
              return [
                {
                  answer,
                  outer,
                  inner: -1,
                },
              ];
            }
          });
        };

        // Flatten the nested set data structure
        const flattenedData = userData.data.flatMap((set: any) => {
          if (!set || typeof set !== "object") return [];

          return Object.keys(set).flatMap((setKey) => {
            const questions = set[setKey];
            if (!questions || typeof questions !== "object") return [];

            return Object.values(questions).flatMap((questionData: any) => {
              // Ensure questionData is an object and has the necessary fields
              if (
                !questionData ||
                !questionData.identifier ||
                !Array.isArray(questionData.answers)
              ) {
                return [];
              }

              const { identifier, answers } = questionData;
              const flattenedAnswersWithIndices =
                flattenAnswersWithIndices(answers);
              const questionDataset = allQuestions.find(
                (q) => q.id === identifier
              );

              return flattenedAnswersWithIndices.map(
                ({ answer, outer, inner }) => ({
                  identifier,
                  answer,
                  setKey,
                  allAnswers: answers,
                  outer,
                  inner,
                  type: questionDataset?.dataset?.[0]?.[0] || "unknown",
                })
              );
            });
          });
        });

        // Additional filtering to remove any remaining empty values
        const filteredData = flattenedData.filter((item) => {
          if (!item.answer) return false;

          // Handle pseudonymized data
          if (
            typeof item.answer === "object" &&
            item.answer.data !== undefined
          ) {
            return !isEmptyValue(item.answer);
          }

          // Handle plain data
          return !isEmptyValue(item.answer);
        });

        /*console.log(
          `User ${userData.user}: ${flattenedData.length} total answers, ${filteredData.length} after filtering`
        );*/

        return {
          ...userData,
          data: filteredData,
        };
      })
      .filter((userData) => {
        const hasData = userData.data.length > 0;
        if (!hasData) {
          console.log(`User ${userData.user}: Filtered out (no valid data)`);
        }
        return hasData;
      });

    console.log(`Final result: ${processedData.length} users with valid data`);
    return processedData;
  } catch (error) {
    console.error("❌ Error in onlyEditable:", error);
    return [];
  }
};

export const allErrorData = async (data, allQuestions) => {
  try {
    console.log("=== PROCESSING ERROR DATA ===");

    // Filter the input data to only include users with error data
    const usersWithErrorData = data
      .map((userData) => {
        if (!userData.data) {
          return { ...userData, data: [] };
        }

        // Function to check if a value has errors (null, undefined)
        const hasErrors = (value) => {
          return value === null || value === undefined;
        };

        // Function to flatten multi-dimensional arrays and track indices
        const flattenAnswersWithIndices = (answers) => {
          return answers.flatMap((answer, outer) => {
            if (Array.isArray(answer)) {
              return answer.map((innerAnswer, inner) => ({
                answer: innerAnswer,
                outer,
                inner,
              }));
            } else {
              return [
                {
                  answer,
                  outer,
                  inner: -1,
                },
              ];
            }
          });
        };

        // Flatten the nested set data structure
        const flattenedData = userData.data.flatMap((set) => {
          return Object.keys(set).flatMap((setKey) => {
            const questions = set[setKey];
            return Object.values(questions).flatMap((questionData) => {
              // Ensure questionData is an object and has the necessary fields
              if (
                !questionData ||
                !questionData.identifier ||
                !Array.isArray(questionData.answers)
              ) {
                return [];
              }

              // Extract identifier and answers, and flatten them with indices
              const { identifier, answers } = questionData;
              const flattenedAnswersWithIndices =
                flattenAnswersWithIndices(answers);
              const questionDataset = allQuestions.find(
                (q) => q.id === identifier
              );

              // Process the answers with the double layer index
              return flattenedAnswersWithIndices.map(
                ({ answer, outer, inner }) => ({
                  identifier,
                  answer,
                  setKey,
                  allAnswers: answers,
                  outer,
                  inner,
                  type: questionDataset?.dataset?.[0]?.[0] || "unknown",
                })
              );
            });
          });
        });

        // Filter error data (null, undefined answers)
        const errorData = flattenedData.filter((item) => {
          // Check for null/undefined in both plain and pseudonymized data
          if (
            typeof item.answer === "object" &&
            item.answer.data !== undefined
          ) {
            return hasErrors(item.answer.data);
          }
          return hasErrors(item.answer);
        });

        console.log(
          `User ${userData.user}: Found ${errorData.length} error items`
        );

        // Return the user data with only error data
        return { ...userData, data: errorData };
      })
      // Filter out users who have no error data
      .filter((userData) => userData.data.length > 0);

    console.log(
      `Final error data: ${usersWithErrorData.length} users with errors`
    );
    return usersWithErrorData;
  } catch (error) {
    console.error("❌ Error in allErrorData:", error);
    return [];
  }
};
